import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {SocialLink} from './ManageMySite.types';

export const ABOUT_MAX_LENGTH = 1000;
export const ABOUT_WARN = 950;
export const DEFAULT_COUNTRY = 'United States';

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const ZIP_REGEX = /^\d{5}(-\d{4})?$/;
export const SLUG_REGEX = /^[A-Za-z0-9]+$/;
export const BLOG_DOMAIN_REGEX =
  /\.(com|org|net|io|co|edu|gov|me|us|info)(\/?.*)?$/i;
export const SOCIAL_USERNAME_REGEX = /^(?=.*[A-Za-z0-9])[A-Za-z0-9._-]{1,50}$/;

export const COUNTRY_OPTIONS = [
  {label: 'United States', value: 'US'},
  {label: 'Hong Kong', value: 'HK'},
  {label: 'Singapore', value: 'SG'},
];

export const COUNTRY_DISPLAY_MAP: Record<string, string> = {
  US: 'United States',
  HK: 'Hong Kong',
  SG: 'Singapore',
};

export const normalizeCountry = (raw?: string): string => {
  if (!raw) return '';
  const map: Record<string, string> = {
    'united states': 'US',
    usa: 'US',
    us: 'US',
    'hong kong': 'HK',
    hk: 'HK',
    singapore: 'SG',
    sg: 'SG',
  };
  const normalized = map[raw.toLowerCase()];
  return normalized ?? raw.trim();
};

export const getCountryLabel = (code?: string): string => {
  const normalized = normalizeCountry(code) || 'US';
  return COUNTRY_DISPLAY_MAP[normalized] || 'United States';
};

export const US_STATES: {label: string; value: string}[] = [
  {label: 'Alabama', value: 'AL'},
  {label: 'Alaska', value: 'AK'},
  {label: 'Arizona', value: 'AZ'},
  {label: 'Arkansas', value: 'AR'},
  {label: 'California', value: 'CA'},
  {label: 'Colorado', value: 'CO'},
  {label: 'Connecticut', value: 'CT'},
  {label: 'Delaware', value: 'DE'},
  {label: 'Florida', value: 'FL'},
  {label: 'Georgia', value: 'GA'},
  {label: 'Hawaii', value: 'HI'},
  {label: 'Idaho', value: 'ID'},
  {label: 'Illinois', value: 'IL'},
  {label: 'Indiana', value: 'IN'},
  {label: 'Iowa', value: 'IA'},
  {label: 'Kansas', value: 'KS'},
  {label: 'Kentucky', value: 'KY'},
  {label: 'Louisiana', value: 'LA'},
  {label: 'Maine', value: 'ME'},
  {label: 'Maryland', value: 'MD'},
  {label: 'Massachusetts', value: 'MA'},
  {label: 'Michigan', value: 'MI'},
  {label: 'Minnesota', value: 'MN'},
  {label: 'Mississippi', value: 'MS'},
  {label: 'Missouri', value: 'MO'},
  {label: 'Montana', value: 'MT'},
  {label: 'Nebraska', value: 'NE'},
  {label: 'Nevada', value: 'NV'},
  {label: 'New Hampshire', value: 'NH'},
  {label: 'New Jersey', value: 'NJ'},
  {label: 'New Mexico', value: 'NM'},
  {label: 'New York', value: 'NY'},
  {label: 'North Carolina', value: 'NC'},
  {label: 'North Dakota', value: 'ND'},
  {label: 'Ohio', value: 'OH'},
  {label: 'Oklahoma', value: 'OK'},
  {label: 'Oregon', value: 'OR'},
  {label: 'Pennsylvania', value: 'PA'},
  {label: 'Rhode Island', value: 'RI'},
  {label: 'South Carolina', value: 'SC'},
  {label: 'South Dakota', value: 'SD'},
  {label: 'Tennessee', value: 'TN'},
  {label: 'Texas', value: 'TX'},
  {label: 'Utah', value: 'UT'},
  {label: 'Vermont', value: 'VT'},
  {label: 'Virginia', value: 'VA'},
  {label: 'Washington', value: 'WA'},
  {label: 'West Virginia', value: 'WV'},
  {label: 'Wisconsin', value: 'WI'},
  {label: 'Wyoming', value: 'WY'},
  {label: 'American Samoa', value: 'AS'},
  {label: 'District of Columbia', value: 'DC'},
  {label: 'Federated States of Micronesia', value: 'FM'},
  {label: 'Guam', value: 'GU'},
  {label: 'Marshall Islands', value: 'MH'},
  {label: 'Northern Mariana Islands', value: 'MP'},
  {label: 'Palau', value: 'PW'},
  {label: 'Puerto Rico', value: 'PR'},
  {label: 'Virgin Islands', value: 'VI'},
];

export const REGISTERED_FIELDS: {label: string; value: string}[] = [
  {
    label: 'A Doctor of Medicine or Osteopathy',
    value: 'doctor-of-medicine-or-osteopathy',
  },
  {label: 'Podiatrists', value: 'podiatrists'},
  {label: 'Dentists', value: 'dentists'},
  {label: 'Clinical Psychologists', value: 'clinical-psychologists'},
  {label: 'Optometrists', value: 'optometrists'},
  {label: 'Chiropractors', value: 'chiropractors'},
  {label: 'Nurse Practitioners', value: 'nurse-practitioners'},
  {label: 'Nurse-Midwives', value: 'nurse-midwives'},
  {label: 'Clinical Social Workers', value: 'clinical-social-workers'},
  {label: 'Physician Assistants', value: 'physician-assistants'},
  {
    label: 'Christian Scientist Practitioners',
    value: 'christian-scientist-practitioners',
  },
  {label: 'Other', value: 'other'},
];

export const LANGUAGES = [
  {label: 'English', value: 'en'},
  {label: 'Espanol', value: 'es'},
];

export const t = (key: string, fallback: string): string =>
  String(tt(key, fallback) ?? fallback);

export const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

export const IMAGE_SPECS = {
  avatar: {
    width: 400,
    height: 400,
    label: 'Profile Photo',
  },
  transformation: {
    width: 1200,
    height: 1600,
    label: 'Transformation Photo',
  },
} as const;

export const formatImageHint = (
  width: number,
  height: number,
  maxFileSizeBytes = MAX_FILE_SIZE_BYTES,
) => {
  const maxMb = Math.floor(maxFileSizeBytes / (1024 * 1024));
  const shape = width === height ? 'square' : 'portrait';
  return `Max ${maxMb}MB. Recommended: ${width} x ${height} ${shape} PNG or JPEG`;
};

export const SOCIAL_MEDIA_PLATFORMS: SocialLink[] = [
  {
    socialNetworkID: 1,
    type: 'Facebook',
    baseUrl: 'https://facebook.com/',
    url: '',
    resolvedUrl: '',
  },
  {
    socialNetworkID: 9,
    type: 'Instagram',
    baseUrl: 'https://instagram.com/',
    url: '',
    resolvedUrl: '',
  },
  {
    socialNetworkID: 3,
    type: 'Twitter',
    baseUrl: 'https://twitter.com/',
    url: '',
    resolvedUrl: '',
  },
  {
    socialNetworkID: 7,
    type: 'YouTube',
    baseUrl: 'https://youtube.com/',
    url: '',
    resolvedUrl: '',
  },
  {
    socialNetworkID: 5,
    type: 'LinkedIn',
    baseUrl: 'https://linkedin.com/',
    url: '',
    resolvedUrl: '',
  },
  {
    socialNetworkID: 8,
    type: 'Pinterest',
    baseUrl: 'https://pinterest.com/',
    url: '',
    resolvedUrl: '',
  },
  {
    socialNetworkID: 6,
    type: 'MySpace',
    baseUrl: 'https://myspace.com/',
    url: '',
    resolvedUrl: '',
  },
  {
    socialNetworkID: 4,
    type: 'Blog',
    baseUrl: 'https://',
    url: '',
    resolvedUrl: '',
  },
];

export const SOCIAL_LINK_ORDER_BY_ID = [1, 9, 3, 7, 5, 8, 6, 4];

export const sortSocialLinks = (links: SocialLink[]) =>
  links?.slice()?.sort((a, b) => {
    const orderMap = Object.fromEntries(
      SOCIAL_LINK_ORDER_BY_ID.map((id, index) => [id, index]),
    );
    return (
      (orderMap[a.socialNetworkID] ?? SOCIAL_LINK_ORDER_BY_ID.length) -
      (orderMap[b.socialNetworkID] ?? SOCIAL_LINK_ORDER_BY_ID.length)
    );
  });
